import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import {
  ChallengeStatusTagComponent,
  ChallengeStatusTagType,
} from '@freelancer/components/challenge-status-tag';
import { PipesModule } from '@freelancer/pipes';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import {
  flGroupOutline,
  flTrophy,
  IconColor,
  IconSize,
  provideFlIcons,
} from '@freelancer/ui/icon';
import { LinkUnderline, QueryParamsHandling } from '@freelancer/ui/link';
import type { PictureBackgroundImageStyles } from '@freelancer/ui/picture';
import { PictureBgImageObjectFit, PictureLayout } from '@freelancer/ui/picture';
import { isDefined } from '@freelancer/utils';
import { COMPACT_CARD_COVER_IMAGE_SIZE } from '../challenge-card.types';

@Component({
  providers: [provideFlIcons({ flGroupOutline, flTrophy })],
  selector: 'fl-challenge-card-compact',
  standalone: true,
  imports: [
    CommonModule,
    ChallengeStatusTagComponent,
    PipesModule,
    TrackingModule,
    UiModule,
  ],
  template: `
    @if (link) {
      <fl-link
        class="LinkWrapper"
        [display]="'flex'"
        [flexDirection]="'column'"
        [flTrackingLabel]="trackingLabel"
        [flTrackingReferenceId]="challengeId"
        [flTrackingReferenceType]="'challenge_id'"
        [link]="link"
        [queryParamsHandling]="QueryParamsHandling.PRESERVE"
        [underline]="LinkUnderline.NEVER"
      >
        <ng-container *ngTemplateOutlet="cardContent"></ng-container>
      </fl-link>
    } @else {
      <ng-container *ngTemplateOutlet="cardContent"></ng-container>
    }

    <ng-template #cardContent>
      <div class="Content">
        @if (imageUrl) {
          <fl-picture
            class="Content-image"
            [backgroundStyles]="backgroundStyles"
            [height]="CompactCardCoverImageSize.height"
            [isBackgroundImage]="true"
            [layout]="PictureLayout.FIXED"
            [resizeFit]="'crop'"
            [src]="imageUrl"
            [width]="CompactCardCoverImageSize.width"
          ></fl-picture>
        }
        <fl-challenge-status-tag
          class="Content-statusTag"
          [status]="status"
        ></fl-challenge-status-tag>
        <div class="Content-bottom">
          <h2
            class="Title uppercase"
            [flMaxLines]="2"
          >
            {{ challengeTitle }}
          </h2>
          <div class="Meta">
            @if (
              prize !== undefined && prize > 0 && currencyCode !== undefined
            ) {
              <div class="Meta-item">
                <p
                  class="Meta-label text-body-12"
                  i18n="Compact challenge card prize label"
                >
                  Prize
                </p>
                <p class="Meta-value text-subtitle-16">
                  {{
                    prize | flCurrency: currencyCode : { withPrecision: false }
                  }}
                </p>
              </div>
            }
            @if (
              startDate !== undefined &&
              !showEntryCount &&
              winnerCount === undefined
            ) {
              <div class="Meta-item">
                @if (startDateHasPassed) {
                  <p
                    class="Meta-label text-body-12"
                    i18n="Compact challenge card started date label"
                  >
                    Date Started
                  </p>
                } @else {
                  <p
                    class="Meta-label text-body-12"
                    i18n="Compact challenge card start date label"
                  >
                    Date Starts
                  </p>
                }
                <p class="Meta-value text-subtitle-16">
                  {{ startDate | date: 'mediumDate' }}
                </p>
              </div>
            }
            @if (isDefined(winnerCount)) {
              <div class="Meta-item">
                <p
                  class="Meta-label text-body-12"
                  i18n="Compact challenge card winners label"
                >
                  Winners
                </p>
                <p class="Meta-value text-subtitle-16">
                  <fl-icon
                    [color]="IconColor.LIGHT"
                    [name]="'flTrophy'"
                    [size]="IconSize.SMALL"
                  ></fl-icon>
                  {{ winnerCount }}
                </p>
              </div>
            } @else if (showEntryCount) {
              <div class="Meta-item">
                <p
                  class="Meta-label text-body-12"
                  i18n="Compact challenge card entries label"
                >
                  Entries
                </p>
                <p class="Meta-value text-subtitle-16">
                  <fl-icon
                    [color]="IconColor.LIGHT"
                    [name]="'flGroupOutline'"
                    [size]="IconSize.SMALL"
                  ></fl-icon>
                  {{ entryCount }}
                </p>
              </div>
            }
          </div>
        </div>
      </div>
    </ng-template>
  `,
  styleUrls: ['./challenge-card-compact.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChallengeCardCompactComponent {
  CompactCardCoverImageSize = COMPACT_CARD_COVER_IMAGE_SIZE;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkUnderline = LinkUnderline;
  PictureLayout = PictureLayout;
  QueryParamsHandling = QueryParamsHandling;

  isDefined = isDefined;

  readonly backgroundStyles: PictureBackgroundImageStyles = {
    position: 'center',
    objectFit: PictureBgImageObjectFit.COVER,
  };

  // FIXME: T300116 Enable hydration.
  // Currently fails when an i18n block follows an empty @if with
  // TypeError: Cannot read properties of null (reading 'nextSibling').
  @HostBinding('attr.ngSkipHydration') ngSkipHydration = true;

  @Input({ required: true }) challengeId: number;
  @Input({ required: true }) challengeTitle: string;
  @Input({ required: true }) status: ChallengeStatusTagType;

  @Input() currencyCode?: string;
  @Input() entryCount?: number;
  @Input() imageUrl?: string;
  @Input() link?: string;
  @Input() startDate?: number;
  @Input() prize?: number;
  @Input() trackingLabel = 'ChallengeCardCompactClicked';
  @Input() winnerCount?: number;

  get startDateHasPassed(): boolean {
    return this.startDate !== undefined && this.startDate <= Date.now();
  }

  get showEntryCount(): boolean {
    return (
      this.entryCount !== undefined &&
      (this.startDate === undefined || this.startDate <= Date.now())
    );
  }
}
